/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronDown, 
  faSignOutAlt, 
  faCheckCircle,
  faComments,
  faCreditCard,
  faCog,
  faInfoCircle,
  faPlus,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useProfilePhoto } from '../context/ProfilePhotoContext';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import '../styles/ModernProfileDropdown.css';

const ModernProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout, isJobSeeker, isRecruiter, isAuthenticated, isEmployer } = useAuth();
  const { profilePhoto, getUserInitials } = useProfilePhoto();

  // Fetch profile data to get professional title
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(buildApiUrl('/api/jobseeker/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data) {
          setProfileData(response.data);
          console.log('✅ Profile data fetched for dropdown:', response.data);
        }
      } catch (error) {
        console.log('⚠️ Could not fetch profile data for dropdown:', error.message);
        // Don't fail silently, just use user data from auth context
      }
    };

    fetchProfileData();
  }, [isAuthenticated, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Don't render if user is not authenticated
  if (!isAuthenticated || !user) {
    console.log('🔐 ModernProfileDropdown - User not authenticated, not rendering');
    return null;
  }

  // Debug: Log user data to see what's available
  console.log('🔍 ModernProfileDropdown - User data:', {
    user: user,
    professionalTitle: user?.professionalTitle,
    currentJobTitle: user?.currentJobTitle,
    role: user?.role,
    isJobSeeker: isJobSeeker()
  });

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const getProfilePath = () => {
    if (isEmployer()) return '/employer/profile';
    if (isRecruiter()) return '/recruiter/profile';
    if (isJobSeeker()) return '/jobseeker/profile';
    return '/profile';
  };

  const getSettingsPath = () => {
    if (isEmployer()) return '/employer/settings';
    if (isRecruiter()) return '/recruiter/settings';
    if (isJobSeeker()) return '/jobseeker/settings';
    return '/settings';
  };

  const getDisplayInitials = () => {
    if (user?.firstName && user?.lastName) {
      return getUserInitials(user.firstName, user.lastName);
    }
    if (user?.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return getUserInitials(nameParts[0], nameParts[1]);
      }
      return getUserInitials(user.name, '');
    }
    return getUserInitials('', '');
  };

  return (
    <div className="modern-profile-dropdown" ref={dropdownRef}>
      <button className="modern-profile-trigger" onClick={toggleDropdown}>
        <div className="modern-avatar">
          {profilePhoto ? (
            <img 
              src={profilePhoto} 
              alt="Profile" 
              className="modern-avatar-image"
            />
          ) : (
            <div className="modern-avatar-placeholder">
              {getDisplayInitials()}
            </div>
          )}
        </div>
        
        <div className="modern-profile-text">
          <span className="modern-profile-label">PROFILE</span>
        </div>
        
        <div className="modern-dropdown-arrow">
          <FontAwesomeIcon 
            icon={faChevronDown} 
            className={`modern-arrow-icon ${isOpen ? 'open' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="modern-dropdown-menu">
          <div className="modern-dropdown-header">
            <div className="modern-header-info">
              <div className="modern-header-name">
                {user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
              </div>
              <div className="modern-header-email">
                {user?.email || 'No email'}
              </div>
            </div>
            <div className="modern-header-avatar">
              {profilePhoto ? (
                <img 
                  src={profilePhoto} 
                  alt="Profile" 
                  className="modern-header-avatar-image"
                />
              ) : (
                <div className="modern-header-avatar-placeholder">
                  {getDisplayInitials()}
                </div>
              )}
            </div>
          </div>
          
          <div className="modern-dropdown-divider"></div>
          
          <div className="modern-dropdown-items">
            <button 
              className="modern-dropdown-item" 
              onClick={() => {
                navigate(getProfilePath());
                setIsOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="modern-item-icon" />
              <span className="modern-item-text">Profile</span>
            </button>
            
            {(isEmployer() || isRecruiter()) && (
              <button 
                className="modern-dropdown-item" 
                onClick={() => {
                  navigate(isEmployer() ? '/employer/community' : '/recruiter/community');
                  setIsOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faComments} className="modern-item-icon" />
                <span className="modern-item-text">Community</span>
                <button className="modern-community-add-btn" onClick={(e) => {
                  e.stopPropagation();
                  navigate(isEmployer() ? '/employer/community/new' : '/recruiter/community/new');
                  setIsOpen(false);
                }}>
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </button>
            )}
            
            <button 
              className="modern-dropdown-item" 
              onClick={() => {
                navigate(isEmployer() ? '/employer/subscription' : isRecruiter() ? '/recruiter/subscription' : '/subscription');
                setIsOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faCreditCard} className="modern-item-icon" />
              <span className="modern-item-text">Subscription</span>
              <span className="modern-pro-badge">
                <FontAwesomeIcon icon={faBolt} className="modern-pro-icon" />
                PRO
              </span>
            </button>
            
            <button 
              className="modern-dropdown-item" 
              onClick={() => {
                navigate(getSettingsPath());
                setIsOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faCog} className="modern-item-icon" />
              <span className="modern-item-text">Settings</span>
            </button>
            
            <button 
              className="modern-dropdown-item" 
              onClick={() => {
                navigate('/help');
                setIsOpen(false);
              }}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="modern-item-icon" />
              <span className="modern-item-text">Help center</span>
            </button>
            
            <div className="modern-dropdown-divider"></div>
            
            <button className="modern-dropdown-item logout-item" onClick={handleLogout}>
              <FontAwesomeIcon icon={faSignOutAlt} className="modern-item-icon" />
              <span className="modern-item-text">Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernProfileDropdown;
